import React, { useEffect, useState } from 'react';
import { bedOptions, bathOptions } from '../../../../domain/filterOptions';
import { Range } from '../../../../domain/FilterTypes';
import { applyBedsClick, BedOptionLabel, isBedOptionSelected } from '../../../../domain/mappers/bedsMapper';
import { applyBathsClick, BathOptionLabel, isBathOptionSelected } from '../../../../domain/mappers/bathMapper';

interface BedsBathBodyProps {
  bedsRange: Range | null;
  bathsRange: Range | null;
  onBedsRangeChange: (range: Range) => void;
  onBathsRangeChange: (range: Range) => void;
}

export const BedsBathBody = ({ bedsRange, bathsRange, onBedsRangeChange, onBathsRangeChange }: BedsBathBodyProps) => {
  const [ bedsSelection, setBedsSelection ] = useState<number[]>([]);

  // Optional: sync bedsSelection from current bedsRange when filter changes externally (e.g. reset)
  useEffect(() => {
    if (!bedsRange || bedsRange.min == null) {
      setBedsSelection([]);
      return;
    }

    if (bedsRange.max == null || bedsRange.min === bedsRange.max) {
      setBedsSelection([ bedsRange.min ]);
    } else {
      setBedsSelection([ bedsRange.min, bedsRange.max ]);
    }
  }, [ bedsRange?.min, bedsRange?.max ]);

  const handleBedsClick = (opt: string) => {
    const { range, nextSelection } = applyBedsClick(
      opt as BedOptionLabel,
      bedsSelection,
    );
    onBedsRangeChange(range);
    setBedsSelection(nextSelection);
  };

  const handleBathsClick = (opt: string) => {
    const range = applyBathsClick(opt as BathOptionLabel);
    onBathsRangeChange(range);
  };

  return (
    <div className="space-y-6">
      {/* Beds */}
      <div id="beds-filter-dropdown-section">
        <h3 className="text-lg font-semibold text-gray-900 pb-2">Beds</h3>
        <div className="text-xs text-gray-500 mb-2">Tap two numbers to select a range</div>
        <div className="inline-flex border rounded overflow-hidden">
          {bedOptions.map((opt, idx) => {
            const selected = isBedOptionSelected(
              opt as BedOptionLabel,
              bedsRange,
              idx,
            );

            return (
              <button
                key={opt}
                type="button"
                onClick={() => handleBedsClick(opt)}
                className={`
                  px-4 py-2 text-sm transition-colors
                  ${selected ? 'bg-blue-100 text-blue-600 font-medium' : 'bg-white text-gray-800 hover:bg-gray-50'}
                  ${idx !== bedOptions.length - 1 ? 'border-r border-gray-200' : ''}
                `}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      {/* Baths */}
      <div id="baths-filter-dropdown-section">
        <h3 className="text-lg font-semibold text-gray-900 pb-2">Baths</h3>
        <div className="inline-flex border rounded overflow-hidden">
          {bathOptions.map((opt, idx) => {
            const selected = isBathOptionSelected(
              opt as BathOptionLabel,
              bathsRange,
              idx,
            );

            return (
              <button
                key={opt}
                type="button"
                onClick={() => handleBathsClick(opt)}
                className={`
                  px-3 py-2 text-sm transition-colors
                  ${selected ? 'bg-blue-100 text-blue-600 font-medium' : 'bg-white text-gray-800 hover:bg-gray-50'}
                  ${idx !== bathOptions.length - 1 ? 'border-r border-gray-200' : ''}
                `}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
